/**
 * @file SoundCatalog.ts
 * @role Defines the audio cue contract and converts Vite-discovered runtime files into strict
 *       WebM+MP3 source pairs. Missing pairs are expected and deliberately remain unavailable.
 * @owns Sound IDs, audio loading/playback types, and filename-to-source validation.
 * @neverDoes Load or play audio. Import skin files. Decide gameplay outcomes.
 * @invariants 1, 8, 13.
 */

export const SOUND_IDS = [
  'ui_click',
  'spin_start',
  'reel_spin_loop',
  'reel_stop',
  'reel_stop_final',
  'win_small',
  'win_medium',
  'win_big',
  'win_jackpot',
  'win_symbol_hit',
  'win_payline_complete',
  'win_count_loop',
  'win_count_end',
  'game_error',
] as const;

export type SoundId = (typeof SOUND_IDS)[number];
export type AudioLoadGroup = 'startup' | 'music' | `feature:${string}`;
export type SoundPlaybackMode = 'oneShot' | 'loop' | 'music';
export type SoundLoadState = 'unavailable' | 'idle' | 'loading' | 'ready' | 'failed';

export interface AudioSourcePair {
  mp3: string;
  webm: string;
}

export type AudioSourceCatalog = Partial<Record<SoundId, AudioSourcePair>>;

export interface AudioSourceCatalogResult {
  readonly sources: AudioSourceCatalog;
  readonly issues: readonly string[];
}

type RuntimeAudioModules = Readonly<Record<string, unknown>>;

const SOUND_ID_SET = new Set<string>(SOUND_IDS);

/**
 * Build strict source pairs from eager Vite URL modules. Runtime files use kebab-case while cue
 * IDs use snake_case. A partial pair is intentionally excluded so unsupported browsers never
 * discover a cue that cannot use the configured fallback.
 */
export function buildAudioSourceCatalog(modules: RuntimeAudioModules): AudioSourceCatalogResult {
  const candidates = new Map<SoundId, Partial<AudioSourcePair>>();
  const issues: string[] = [];

  for (const [modulePath, imported] of Object.entries(modules)) {
    if (typeof imported !== 'string') {
      issues.push(`Audio source "${modulePath}" did not resolve to a URL`);
      continue;
    }
    const filename = modulePath.slice(modulePath.lastIndexOf('/') + 1);
    const match = /^([a-z0-9]+(?:-[a-z0-9]+)*)\.(webm|mp3)$/.exec(filename);
    if (!match) {
      issues.push(`Unknown runtime audio filename "${filename}"`);
      continue;
    }
    const cueName = match[1]?.replaceAll('-', '_');
    const format = match[2] as keyof AudioSourcePair | undefined;
    if (!cueName || !format || !SOUND_ID_SET.has(cueName)) {
      issues.push(`Runtime audio "${filename}" does not match a configured cue`);
      continue;
    }
    const soundId = cueName as SoundId;
    const pair = candidates.get(soundId) ?? {};
    if (pair[format]) {
      issues.push(`Duplicate ${format.toUpperCase()} source for "${soundId}"`);
      continue;
    }
    pair[format] = imported;
    candidates.set(soundId, pair);
  }

  const sources: AudioSourceCatalog = {};
  for (const [soundId, pair] of candidates) {
    if (!pair.webm || !pair.mp3) {
      const missing = pair.webm ? 'MP3' : 'WebM';
      issues.push(`Audio cue "${soundId}" is missing its ${missing} fallback and is disabled`);
      continue;
    }
    sources[soundId] = { webm: pair.webm, mp3: pair.mp3 };
  }

  return { sources, issues };
}

export function isSoundId(value: string): value is SoundId {
  return SOUND_ID_SET.has(value);
}
